import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PageableTableComponent } from '../../../../shared/controls/pageable-table/pageable-table.component';
import { SidePanelComponent } from '../../../../shared/controls/side-panel/side-panel.component';
import { SwitchControlComponent } from '../../../../shared/controls/switch-control/switch-control.component';

import { DialogService } from '../../../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../shared/services/error-service';
import { InitializerService } from '../../../../shared/services/initializer-service';

import { PagingData, ClientAreaService, OptionalReportItem, UpdateClientAreaReportsInput, ActiveReportItem } from '../../../../../service/proxy';

@Component({
    selector: 'client-area-report-list',
    templateUrl: './client-area-report.component.html',
    styleUrl: './client-area-report.component.scss',

    imports: [
        CommonModule,
        PageableTableComponent,
        FormsModule,
        SidePanelComponent,
        SwitchControlComponent
    ],

    providers: [
        ClientAreaService
    ]
})
export class ClientAreaReportComponent implements OnInit {
    public policyId: number = 0;
    public paging: PagingData = new PagingData();

    public activeReports: ActiveReportItem[] = [];
    public optionalReports: OptionalReportItem[] = [];

    public showSidePanel: boolean = false;
    public panelCaption: string = 'Manage policy reports';
    public search: string = '';

    constructor(
        private route: ActivatedRoute,
        private toastService: ToastService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private clientAreaService: ClientAreaService)
    {
        this.paging = this.initializerService.createPagingData();
    }

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.policyId = Number(params.get('policyId'));

            this.getOptionalReports();
        });
    }

    getActiveReports(pagingData: PagingData = new PagingData): void {
        if (!this.policyId || this.policyId <= 0) {
            return;
        }

        this.clientAreaService.getActiveReports(this.policyId, this.search, pagingData.pageNo, pagingData.pageSize, pagingData.sortDirection, pagingData.sortColumn)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.activeReports = data.results || [];
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.activeReports = [];
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            }
        );
    }

    getOptionalReports(): void {
        if (!this.policyId || this.policyId <= 0) {
            return;
        }

        this.clientAreaService.getOptionalReports(this.policyId, this.search)
            .subscribe({
                next: (response: any) => {
                    this.optionalReports = response.results;
                },
                error: (error: any) => {
                    if (error.status === 204) {
                        this.optionalReports = [];
                    }
                    else if (error.status === 400) {
                        this.dialogService.show('Error', 'Bad Request', 'Error');
                    }
                    else if (error.status === 404) {
                        this.dialogService.show('Error', 'Internal Server Error', 'Error');
                    }
                    else {
                        this.dialogService.show('Error', 'An unexpected error occurred', 'Error');
                    }
                }
            }
        );
    }

    toggleSidePanel(): void {
        this.showSidePanel = !this.showSidePanel;
    }

    onSidePanelDone(): void {
        const input: UpdateClientAreaReportsInput = new UpdateClientAreaReportsInput();

        input.reportIds = this.optionalReports
            .filter((report: OptionalReportItem) => report.isSelected)
            .map((report: OptionalReportItem) => report.id);

        this.clientAreaService.updateClientAreaReports(this.policyId, input)
            .subscribe({
                next: (result: any) => {
                    this.toastService.show('Optional reports updated successfully.', 'success');
                    this.toggleSidePanel();
                },
                error: (error: any) => {
                    if (error.status === 400) {
                        this.dialogService.show('Error', 'Bad Request', 'Error');
                    }
                    else if (error.status === 404) {
                        this.dialogService.show('Error', 'Internal Server Error', 'Error');
                    }
                    else {
                        this.dialogService.show('Error', 'An unexpected error occurred', 'Error');
                    }
                },
                complete: () => {
                    this.getOptionalReports();
                    this.getActiveReports(this.paging);
                }
            });
    }

    onSidePanelCancel(): void {
        this.toggleSidePanel();
    }

    getColumnMap() {
        return {
            reportName: 'Report Name',
            isRequired: 'Is Mandatory',
        };
    }
}
